/**
 * Async blocking queue for buffering audio chunks.
 * Dequeue blocks until an item is available or the queue is aborted.
 */
export class AsyncBlockingQueue<T> {
  private promises: Promise<T>[] = [];
  private resolvers: ((t: T) => void)[] = [];
  // Rejectors are needed to unblock pending dequeue() calls when abort() is called.
  // Without this, dispose() would leave the upload loop hanging forever on await dequeue().
  private rejectors: ((reason: Error) => void)[] = [];
  private aborted = false;

  get length(): number {
    return this.promises.length - this.resolvers.length;
  }

  enqueue(item: T): void {
    // Ignore new items after abort - the queue is shutting down
    if (this.aborted) {
      return;
    }

    if (!this.resolvers.length) {
      this.addPromise();
    }
    const resolve = this.resolvers.shift();
    // Also remove the corresponding rejector since we're resolving
    this.rejectors.shift();
    if (!resolve) {
      throw new Error('Resolver is undefined');
    }
    resolve(item);
  }

  dequeue(): Promise<T> {
    // Fast-fail if already aborted - prevents new blocking waits
    if (this.aborted) {
      return Promise.reject(new Error('Queue aborted'));
    }

    if (!this.promises.length) {
      this.addPromise();
    }
    const promise = this.promises.shift();
    if (!promise) {
      throw new Error('Promise is undefined');
    }
    return promise;
  }

  /**
   * Abort the queue - rejects all pending dequeue() calls.
   * This is essential for cleanup: without it, any code awaiting dequeue()
   * would hang indefinitely when we want to stop the uploader.
   */
  abort(): void {
    this.aborted = true;
    const abortError = new Error('Queue aborted');
    // Reject all pending dequeues so they can exit gracefully
    this.rejectors.forEach((reject) => reject(abortError));
    // Clear arrays to release memory
    this.promises = [];
    this.resolvers = [];
    this.rejectors = [];
  }

  isEmpty(): boolean {
    return this.promises.length === 0;
  }

  isBlocked(): boolean {
    return this.resolvers.length > 0;
  }

  isAborted(): boolean {
    return this.aborted;
  }

  private addPromise(): void {
    this.promises.push(
      new Promise((resolve, reject) => {
        this.resolvers.push(resolve);
        this.rejectors.push(reject);
      }),
    );
  }
}
